import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTheme } from '../../components/ThemeProvider';
import Button from '../common/Button';
import { fetchData } from '../../utils/api';

function CartSummary() {
  const { cart, getTotal } = useCart();
  const { formatPrice } = useCurrency();
  const { primaryColor } = useTheme();
  const [business, setBusiness] = useState({ name: '', abn: '' });
  const [vatRate, setVatRate] = useState(0);
  const [shippingFee, setShippingFee] = useState(null);
  const [error, setError] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);

  const safeFormatPrice = (price) => {
    if (!formatPrice) {
      console.warn('CartSummary: formatPrice is undefined, defaulting to USD');
      return `$${Number(price).toFixed(2)}`;
    }
    return formatPrice(price);
  };

  const fetchBusinessDetails = async () => {
    setIsRetrying(false);
    try {
      const data = await fetchData('/website/business-details', { noCache: true });
      console.log(data)
      if (!data?.business) {
        throw new Error('Business details missing in response');
      }
      setBusiness(data.business || { name: 'EcoMart', abn: 'Unknown' });
      setVatRate(data.fees?.vat?.vatRate || 0);
      setShippingFee(data.fees?.shipping?.shippingFee || 0);
      setError('');
    } catch (err) {
      console.error('CartSummary: Failed to fetch business details:', err.message, err);
      setError('Failed to load business details or fees. Using defaults.');
      setBusiness({ name: 'EcoMart', abn: 'Unknown' });
      setVatRate(0);
      setShippingFee(0);
    }
  };

  useEffect(() => {
    fetchBusinessDetails();
  }, []);

  const handleRetry = () => {
    setIsRetrying(true);
    setError('');
    fetchBusinessDetails();
  };

  const subtotal = getTotal();
  const vat = (subtotal * vatRate/100);
  const shipping = shippingFee || 0;
  const total = subtotal + vat + shipping;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-playfair-display text-primary mb-2">Order Summary</h2>
      <p className="text-sm font-montserrat text-primary/70 mb-4">
        {business.name} (TEL: {business.abn})
      </p>
      {error && (
        <div className="mb-4">
          <p className="text-sm font-montserrat text-red-600">{error}</p>
          <Button
            onClick={handleRetry}
            className="mt-2 bg-primary text-cream px-4 py-2 text-sm font-montserrat rounded-full hover:bg-primary/90"
            disabled={isRetrying}
            aria-label="Retry loading business details"
            style={{ '--primary': primaryColor }}
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </Button>
        </div>
      )}
      <div className="space-y-2 font-montserrat text-secondary mb-4">
        <div className="flex justify-between text-lg">
          <span>Subtotal</span>
          <span>{safeFormatPrice(subtotal)}</span>
        </div>
        <div className="flex justify-between text-lg">
          <span>VAT ({vatRate}%)</span>
          <span>{safeFormatPrice(vat)}</span>
        </div>
        <div className="flex justify-between text-lg">
          <span>Shipping</span>
          <span>
            {shippingFee === null ? 'Calculated at checkout' : safeFormatPrice(shipping)}
          </span>
        </div>
        <div className="flex justify-between text-xl font-semibold border-t border-primary/20 pt-2">
          <span>Total</span>
          <span>{safeFormatPrice(total)}</span>
        </div>
      </div>
      <Link to="/checkout">
        <Button
          className="w-full bg-primary text-cream px-6 py-3 text-lg font-montserrat rounded-full hover:bg-primary/90 disabled:opacity-50"
          disabled={cart.length === 0}
          aria-label={cart.length === 0 ? 'Checkout disabled (cart is empty)' : 'Proceed to checkout'}
          style={{ '--primary': primaryColor }}
        >
          Proceed to Checkout
        </Button>
      </Link>
      {cart.length > 0 && (
        <Link
          to="/shop"
          className="block text-center mt-4 text-primary font-montserrat hover:underline"
          aria-label="Continue shopping from cart summary"
        >
          Continue Shopping
        </Link>
      )}
    </div>
  );
}

export default CartSummary;